// @ts-strict-ignore
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { switchMap } from 'rxjs';
import { DEFAULT_LANGUAGE } from '../../../../app.constants';
import { getOldFeedbackPrivacyNoticeHtml } from '../../../../functions/get-old-feedback-privacy-notice-html';
import { UI_STRINGS } from '../../../../functions/language/uistrings';
import { toHtml } from '../../../../functions/to-html';
import { Language } from '../../../../model/enums/language';
import { FeedbackSettings } from '../../../../model/feedback-settings';
import { FeedbackTagPattern } from '../../../../model/feedback-tag-pattern';
import { SendState } from '../../../../model/send-state';
import { ValidationRegex } from '../../../../model/validation-regex';
import { BackendBoardsV2Service } from '../../../../services/backend/backend-boards-v2.service';
import { BackendBoardsService } from '../../../../services/backend/backend-boards.service';
import { TranslationService } from '../../../../services/translation-service';
import { migrationStatusActions } from '../../../../store/migration-status/migration-status.actions';
import { selectActiveBoardMigrationStatus } from '../../../../store/migration-status/migration-status.selectors';
import { FeedbackFormData } from './feedback-form-data';

@Component({
    selector: 'app-feedback',
    templateUrl: './feedback.component.html',
    styleUrls: ['./feedback.component.scss'],
    standalone: false
})
export class FeedbackComponent implements OnInit {
  @Input() boardId: string;
  @Input() lang: Language = DEFAULT_LANGUAGE;
  @Input() set feedbackSettings(fs: FeedbackSettings) {
    if (fs.privacyNotice && fs.organisation && fs.privacyPolicyURL) {
      const isValidOldNotice =
        fs.privacyNotice.match(FeedbackTagPattern.Link) && fs.privacyNotice.match(FeedbackTagPattern.Org);
      // 20204-08-27: Commented out as we do not know if or how we want to validate the privacy notice
      // The old notice validation is kept for backwards compatibility since it included our own custom tags
      // const isValidNewNotice =
      //   fs.privacyNotice.includes(fs.organisation) && fs.privacyNotice.includes(fs.privacyPolicyURL);

      if (isValidOldNotice) {
        const oldNoticeParams = { notice: fs.privacyNotice, organisation: fs.organisation, link: fs.privacyPolicyURL };
        this.privacyNotice = toHtml(getOldFeedbackPrivacyNoticeHtml(oldNoticeParams));
      }
      // else if (isValidNewNotice) {
      this.privacyNotice = toHtml(fs.privacyNotice);
      // }
    } else {
      this.feedbackForm.get('privacyAgreement').setValue(true);
    }
    this.organisationName = fs.organisation;
  }

  feedbackForm: UntypedFormGroup;
  messageTypes: string[];
  organisationName: string;
  privacyNotice: string;

  private _alreadySentMessage: string;
  private _isSending: boolean = false;
  private _sendingMessage: string = '';
  private _sendState: SendState = SendState.Unsent;

  readonly BOARD_STRINGS = UI_STRINGS.BOARD;
  readonly FEEDBACK_FORM_STRINGS = UI_STRINGS.FEEDBACK_FORM;
  readonly GLOBAL_STRINGS = UI_STRINGS.GLOBAL;

  get hasSendError(): boolean {
    return this._sendState === SendState.SendError;
  }

  get isSameAsAlreadySent(): boolean {
    return JSON.stringify(this._buildFeedbackFormData()) === this._alreadySentMessage;
  }

  get notification(): string {
    switch (this._sendState) {
      case SendState.Unsent:
        return '';
      case SendState.SendOk:
        return this._translate(this.FEEDBACK_FORM_STRINGS.SEND_OK);
      case SendState.SendError:
        return this._translate(this.FEEDBACK_FORM_STRINGS.SEND_ERROR);
      default:
        return '';
    }
  }

  get sendCaption(): string {
    return this._isSending
      ? this._translate(this.FEEDBACK_FORM_STRINGS.SENDING)
      : this._translate(this.FEEDBACK_FORM_STRINGS.SEND);
  }

  set sendState(value: SendState) {
    this._sendState = value;
    if (value === SendState.SendOk) {
      this._alreadySentMessage = this._sendingMessage;
    }
  }
  get sendState(): SendState {
    return this._sendState;
  }

  constructor(
    private _backendBoardsService: BackendBoardsService,
    private _backendBoardsV2Service: BackendBoardsV2Service,
    private _store: Store,
    private _translationService: TranslationService
  ) {
    this.feedbackForm = new UntypedFormGroup(
      {
        messageType: new UntypedFormControl(null, Validators.required),
        subject: new UntypedFormControl(''),
        message: new UntypedFormControl('', [Validators.required, Validators.maxLength(1000)]),
        name: new UntypedFormControl('', Validators.required),
        email: new UntypedFormControl('', [Validators.required, Validators.pattern(ValidationRegex.email)]),
        privacyAgreement: new UntypedFormControl(false, Validators.requiredTrue),
      },
      { updateOn: 'blur' }
    );
  }

  ngOnInit(): void {
    this.messageTypes = [
      this._translate(this.FEEDBACK_FORM_STRINGS.MESSAGE_TYPE_QUESTION),
      this._translate(this.FEEDBACK_FORM_STRINGS.MESSAGE_TYPE_FEEDBACK),
    ];
    this.feedbackForm.get('messageType').setValue(this.messageTypes[0], { emitEvent: false, onlySelf: true });

    this.feedbackForm.valueChanges.subscribe(() => this._updateSendState());
  }

  send() {
    if (this.feedbackForm.valid) {
      this.sendState = SendState.Unsent;
      const data = this._buildFeedbackFormData();
      this._sendingMessage = JSON.stringify(data);
      this._isSending = true;
      const body = { email: data.senderEmail, message: data.message, name: data.senderName, subject: data.subject };
      this._store
        .select(selectActiveBoardMigrationStatus)
        .pipe(
          switchMap(({ boardId, migrationStatus }) => {
            if (boardId === this.boardId && !migrationStatus) {
              return this._backendBoardsService.getBoardMigrationStatus(this.boardId).pipe(
                switchMap((migrationStatus) => {
                  this._store.dispatch(
                    migrationStatusActions.loadedMigrationStatus({ boardId: this.boardId, migrationStatus })
                  );
                  return this._backendBoardsV2Service.postFeedback(this.boardId, body);
                })
              );
            } else {
              return this._backendBoardsV2Service.postFeedback(this.boardId, body);
            }
          })
        )
        .subscribe((result) => {
          this._isSending = false;
          if (result) {
            this.feedbackForm.reset();
            this.feedbackForm.get('messageType').setValue(this.messageTypes[0]);
            this.sendState = SendState.SendOk;
            if (!this.privacyNotice) {
              this.feedbackForm.get('privacyAgreement').setValue(true);
            }
          } else {
            this.sendState = SendState.SendError;
          }
        });
    }
  }

  private _buildFeedbackFormData(): FeedbackFormData {
    return {
      subject: this.feedbackForm.value.messageType + ': ' + this.feedbackForm.value.subject,
      message: this.feedbackForm.value.message,
      senderName: this.feedbackForm.value.name,
      senderEmail: this.feedbackForm.value.email,
    };
  }

  private _updateSendState() {
    this._sendState =
      JSON.stringify(this._buildFeedbackFormData()) === this._alreadySentMessage ? SendState.SendOk : SendState.Unsent;
  }

  private _translate(stringId: string): string {
    return this._translationService.translate(this.lang, stringId);
  }
}
